// ChunkLoadErrorBoundary：捕获 lazyWithRetry 抛出的 ChunkLoadError，
// 提示用户刷新页面以加载最新 chunk manifest。
//
// 用法：在 __root.tsx 或主 layout 外层包裹。

import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/Button";

interface State {
  hasError: boolean;
  error: unknown;
}

function isChunkError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { name?: string; message?: string };
  if (e.name === "ChunkLoadError") return true;
  if (
    typeof e.message === "string" &&
    /Loading chunk|Failed to fetch dynamically imported module/i.test(e.message)
  ) {
    return true;
  }
  return false;
}

export class ChunkLoadErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: unknown): State {
    if (isChunkError(error)) return { hasError: true, error };
    // 非 chunk 错误向上抛，让上层 boundary 或全局 handler 处理
    throw error;
  }

  reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="m-6 max-w-md rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 text-sm">
        <div className="font-semibold text-[hsl(var(--foreground))]">
          界面资源已更新
        </div>
        <p className="mt-2 text-[hsl(var(--muted-foreground))]">
          已加载的页面缓存了旧版本静态资源，无法继续访问。请刷新页面获取最新版本。
        </p>
        <div className="mt-3">
          <Button size="sm" onClick={this.reload}>
            刷新页面
          </Button>
        </div>
      </div>
    );
  }
}
